// src/utils/IconCacheManager.ts
/**
 * Icon Cache Manager
 * 
 * Caches icon images to prevent repeated network requests
 * and improve performance when switching between icons
 */

class IconCacheManager {
    private cache: Map<string, string> = new Map();
    private loadingPromises: Map<string, Promise<string>> = new Map();

    /**
     * Get cached icon or load it
     * @param url - Icon URL
     * @returns Promise with data URL or original URL
     */
    async getIcon(url: string): Promise<string> {
        // Return from cache if available
        if (this.cache.has(url)) {
            return this.cache.get(url)!;
        }

        // If already loading, return existing promise
        if (this.loadingPromises.has(url)) {
            return this.loadingPromises.get(url)!;
        }

        // Load and cache the icon
        const loadPromise = this.loadAndCacheIcon(url);
        this.loadingPromises.set(url, loadPromise);

        try {
            const result = await loadPromise;
            this.loadingPromises.delete(url);
            return result;
        } catch (error) {
            this.loadingPromises.delete(url);
            throw error;
        }
    }

    /**
     * Load icon and convert to data URL for caching
     */
    private async loadAndCacheIcon(url: string): Promise<string> {
        try {
            // For SVG files, fetch and convert to data URL
            if (url.endsWith('.svg')) {
                const response = await fetch(url);
                if (!response.ok) {
                    throw new Error(`Failed to load: ${response.status}`);
                }

                const svgText = await response.text();
                const dataUrl = `data:image/svg+xml;base64,${btoa(svgText)}`;

                this.cache.set(url, dataUrl);
                return dataUrl;
            } else {
                // For non-SVG images, just cache the URL
                // Browser will handle caching
                this.cache.set(url, url);
                return url;
            }
        } catch (error) {
            console.error('❌ Failed to cache icon:', url, error);
            // Return original URL as fallback
            return url;
        }
    }

    /**
     * Preload multiple icons
     */
    async preloadIcons(urls: string[]): Promise<void> {
        const promises = urls.map(url => this.getIcon(url).catch(err => {
            console.warn('Failed to preload:', url, err);
        }));
        await Promise.all(promises);
    }

    /**
     * Clear cache
     */
    clearCache(): void {
        this.cache.clear();
        this.loadingPromises.clear();
    }

    /**
     * Get cache statistics
     */
    getStats() {
        return {
            cachedIcons: this.cache.size,
            loadingIcons: this.loadingPromises.size,
            totalMemory: this.estimateMemoryUsage()
        };
    }

    /**
     * Estimate memory usage (rough calculation)
     */
    private estimateMemoryUsage(): string {
        let totalBytes = 0;
        this.cache.forEach((dataUrl) => {
            // Rough estimate: data URL length ≈ file size
            totalBytes += dataUrl.length;
        });

        const kb = totalBytes / 1024;
        const mb = kb / 1024;

        if (mb > 1) {
            return `${mb.toFixed(2)} MB`;
        }
        return `${kb.toFixed(2)} KB`;
    }
}

// Export singleton instance
export const iconCacheManager = new IconCacheManager();